import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, RefreshCw, Info, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import useSEO from "@/lib/seo";

// Center on "Quartier am Hornschen Tor" construction site
// (between Hornsche Straße, Grabenstraße and Baumstraße in Detmold-Nord)
const QUARTIER_CENTER = [51.9325, 8.8810];
const INITIAL_ZOOM = 13;
const MIN_ZOOM = 12;
const MAX_ZOOM = 19;
const DETMOLD_BOUNDS = [
  [51.88, 8.78], // SW
  [52.00, 8.97], // NE
];

// Traffic tiles are proxied through the backend (15-min cache, no API key
// leaked to the browser, no automatic polling).
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TILE_URL = `${BACKEND_URL}/api/tomtom/traffic-tile/relative-delay/{z}/{x}/{y}.png?thickness=8`;
// Rate-limited by the backend to once per 30 seconds per IP.
const REFRESH_COOLDOWN_MS = 30 * 1000;

function fmtTime(d) {
  try {
    return d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

export default function Liveverkehr() {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const trafficLayerRef = useRef(null);
  const sperrungenLayerRef = useRef(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [refreshing, setRefreshing] = useState(false);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [sperrungen, setSperrungen] = useState([]);
  const [trafficUnavailable, setTrafficUnavailable] = useState(false);
  useSEO({
    title: "LIVE-Verkehr Detmold",
    description: "Aktuelle Verkehrslage in Detmold rund um das Quartier am Hornschen Tor — Live-Karte mit Staus, Sperrungen und Umleitungen.",
    path: "/liveverkehr",
  });

  useEffect(() => {
    api.get("/sperrungen", { params: { only_active: true } })
      .then((r) => setSperrungen(r.data || []))
      .catch(() => setSperrungen([]));
  }, []);

  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;

    const map = L.map(containerRef.current, {
      center: QUARTIER_CENTER,
      zoom: INITIAL_ZOOM,
      minZoom: MIN_ZOOM,
      maxZoom: MAX_ZOOM,
      maxBounds: DETMOLD_BOUNDS,
      maxBoundsViscosity: 0.9,
      zoomControl: false,
      attributionControl: true,
    });
    mapRef.current = map;

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: MAX_ZOOM,
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>-Mitwirkende',
    }).addTo(map);

    // Traffic flow overlay via backend proxy (15-min cache).
    trafficLayerRef.current = L.tileLayer(TILE_URL, {
      maxZoom: MAX_ZOOM,
      opacity: 0.85,
      attribution: "Traffic © TomTom",
    });
    let firstOk = false;
    trafficLayerRef.current.on("tileload", () => {
      if (!firstOk) { firstOk = true; setTrafficUnavailable(false); }
    });
    trafficLayerRef.current.on("tileerror", () => {
      if (!firstOk) setTrafficUnavailable(true);
    });
    trafficLayerRef.current.addTo(map);

    const pin = L.divIcon({
      className: "detmold-pin",
      html: `<span></span>`,
      iconSize: [16, 16],
      iconAnchor: [8, 8],
    });
    L.marker(QUARTIER_CENTER, { icon: pin }).addTo(map)
      .bindPopup('<div style="font-family:inherit;"><strong>Quartier am Hornschen Tor</strong><br/><span style="color:#6b7280;font-size:12px;">Bauvorhaben Detmold</span></div>');

    sperrungenLayerRef.current = L.layerGroup().addTo(map);

    L.control.zoom({ position: "bottomright" }).addTo(map);

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const layer = sperrungenLayerRef.current;
    if (!layer) return;
    layer.clearLayers();

    sperrungen.forEach((s) => {
      if (typeof s.lat !== "number" || typeof s.lng !== "number") return;
      const severity = s.severity || "closure";
      const colorClass = severity === "warning" ? "warning" : severity === "detour" ? "detour" : "closure";
      const icon = L.divIcon({
        className: `sperrung-marker sperrung-${colorClass}`,
        html: `
          <span class="sperrung-pulse"></span>
          <span class="sperrung-core">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
              <line x1="12" y1="9" x2="12" y2="13"></line>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
          </span>`,
        iconSize: [32, 32],
        iconAnchor: [16, 16],
      });
      const m = L.marker([s.lat, s.lng], { icon, riseOnHover: true });
      const period = (s.start_date || s.end_date)
        ? `<div style="color:#6b7280;font-size:11px;margin-top:4px;">${s.start_date || ""}${s.start_date && s.end_date ? " – " : ""}${s.end_date || ""}</div>`
        : "";
      const desc = s.description
        ? `<div style="color:#374151;font-size:12px;margin-top:6px;line-height:1.4;">${s.description}</div>`
        : "";
      m.bindPopup(
        `<div style="font-family:inherit;min-width:200px;max-width:280px;">
           <strong style="color:#7f1d1d;">${s.title}</strong>
           ${period}
           ${desc}
         </div>`
      );
      layer.addLayer(m);
    });
  }, [sperrungen]);

  function refresh() {
    if (!mapRef.current || !trafficLayerRef.current) return;
    if (Date.now() < cooldownUntil) return;
    setRefreshing(true);
    // Ask the backend for a fresh (uncached) tile via ?refresh=1.
    // Cache-buster ensures the browser doesn't return its own cached PNG.
    trafficLayerRef.current.setUrl(
      `${BACKEND_URL}/api/tomtom/traffic-tile/relative-delay/{z}/{x}/{y}.png?thickness=8&refresh=1&_=${Date.now()}`
    );
    setLastUpdate(new Date());
    setCooldownUntil(Date.now() + REFRESH_COOLDOWN_MS);
    setTimeout(() => setRefreshing(false), 800);
  }

  const cooldownActive = Date.now() < cooldownUntil;

  return (
    <div className="bg-white" data-testid="liveverkehr-page">
      <div className="container-narrow pt-12 lg:pt-16 pb-6">
        <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--brand-green-strong))] mb-3">Verkehr</p>
        <h1 className="font-semibold text-3xl sm:text-4xl lg:text-5xl mb-3">LIVE-Verkehr Detmold</h1>
        <p className="text-neutral-600 max-w-2xl text-sm sm:text-base leading-relaxed">
          Interaktive Live-Karte der Verkehrslage im Stadtgebiet Detmold. Grün steht für freie Fahrt, Orange für dichten Verkehr, Rot für Stau.
          {trafficUnavailable && (
            <span className="block mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2" data-testid="liveverkehr-traffic-unavailable">
              Aktuell sind keine Live-Verkehrsdaten verfügbar — die Detmold-Karte inkl. Sperrungen bleibt vollständig nutzbar.
            </span>
          )}
        </p>
      </div>

      <div className="container-narrow pb-12 lg:pb-20">
        <div className="relative w-full border border-brand-line overflow-hidden" style={{ height: "min(72vh, 720px)" }}>
          <div ref={containerRef} className="absolute inset-0 z-0" data-testid="liveverkehr-map" />

          <div
            aria-hidden="true"
            className="absolute inset-0 z-[400] pointer-events-none"
            style={{
              boxShadow: "inset 0 0 120px 24px rgba(0,0,0,0.35)",
              background:
                "linear-gradient(180deg, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0) 18%, rgba(0,0,0,0) 78%, rgba(0,0,0,0.18) 100%)",
            }}
          />

          <div className="absolute top-4 left-4 z-[500] bg-white/95 backdrop-blur border border-brand-line shadow-[0_4px_20px_rgba(0,0,0,0.08)] px-4 py-3 max-w-[300px]" data-testid="liveverkehr-info">
            <div className="flex items-center gap-2 text-xs text-neutral-700 mb-1">
              <span className="inline-block h-2 w-2 rounded-full bg-[hsl(var(--brand-green))] animate-pulse" />
              Aktuelle Verkehrslage in Detmold
            </div>
            <p className="text-[11px] text-neutral-500" data-testid="liveverkehr-last-updated">
              Verkehrslage zuletzt aktualisiert: {fmtTime(lastUpdate)} Uhr
            </p>
          </div>

          <div className="absolute bottom-4 left-4 z-[500] bg-white/95 backdrop-blur border border-brand-line shadow-[0_4px_20px_rgba(0,0,0,0.08)] px-4 py-3 text-[12px] text-neutral-700" data-testid="liveverkehr-legend">
            <div className="flex items-center gap-2 mb-1.5"><span className="inline-block h-2.5 w-5 bg-[#22C55E] rounded-sm" /> Freie Fahrt</div>
            <div className="flex items-center gap-2 mb-1.5"><span className="inline-block h-2.5 w-5 bg-[#F59E0B] rounded-sm" /> Dichter Verkehr</div>
            <div className="flex items-center gap-2 mb-1.5"><span className="inline-block h-2.5 w-5 bg-[#EF4444] rounded-sm" /> Stau</div>
            <div className="flex items-center gap-2 pt-1.5 mt-1.5 border-t border-brand-line">
              <span className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-[#DC2626] text-white">
                <AlertTriangle className="h-2.5 w-2.5" />
              </span>
              Straßensperrung
            </div>
          </div>

          <button
            onClick={refresh}
            disabled={cooldownActive || refreshing}
            data-testid="liveverkehr-refresh"
            title={cooldownActive ? "Bitte einen Moment warten — die Verkehrslage kann nur alle 30 Sekunden neu geladen werden." : "Verkehrslage jetzt aktualisieren"}
            className="absolute top-4 right-4 z-[500] inline-flex items-center gap-2 bg-white/95 backdrop-blur border border-brand-line shadow-[0_4px_20px_rgba(0,0,0,0.08)] px-3 py-2 text-xs text-neutral-700 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
            {cooldownActive ? "Kurz warten…" : "Verkehrslage jetzt aktualisieren"}
          </button>
        </div>

        <div className="mt-6 flex flex-wrap items-start gap-6 text-xs text-neutral-500">
          <p className="flex items-start gap-2 max-w-2xl">
            <Info className="h-4 w-4 mt-0.5 shrink-0 text-[hsl(var(--brand-green-strong))]" />
            <span>
              Kartenbasis: OpenStreetMap-Mitwirkende. Die Verkehrsdaten stammen von TomTom und werden zum Schutz der Datenkontingente <strong>nur auf Anforderung</strong> geladen — mit einem gemeinsamen 15-Minuten-Cache im Backend. Nutzen Sie den Button oben rechts, um bewusst eine aktuelle Verkehrslage abzurufen.
            </span>
          </p>
          <p className="flex items-start gap-2">
            <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-[hsl(var(--brand-green-strong))]" />
            <span>Kartenmittelpunkt: Quartier am Hornschen Tor (51,9325° N · 8,8810° O)</span>
          </p>
        </div>
      </div>

      <style>{`
        .detmold-pin span {
          display: block;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: hsl(var(--brand-green));
          border: 3px solid #fff;
          box-shadow: 0 0 0 2px rgba(0,0,0,0.15), 0 4px 10px rgba(0,0,0,0.25);
        }
        .leaflet-container { font-family: inherit; background: #e8eef2; }
        .leaflet-control-attribution { font-size: 10px !important; background: rgba(255,255,255,0.85) !important; }
        .leaflet-control-zoom a { background: rgba(255,255,255,0.95) !important; color: hsl(var(--brand-ink)) !important; border-color: hsl(var(--brand-line)) !important; }
        .leaflet-control-zoom a:hover { background: hsl(var(--brand-soft)) !important; }

        .sperrung-marker { position: relative; }
        .sperrung-marker .sperrung-pulse {
          position: absolute; inset: 0;
          border-radius: 50%;
          animation: sperrungPulse 1.8s ease-out infinite;
          opacity: 0.55;
        }
        .sperrung-marker .sperrung-core {
          position: relative;
          display: flex; align-items: center; justify-content: center;
          width: 32px; height: 32px;
          border-radius: 50%;
          color: #fff;
          border: 2px solid #fff;
          box-shadow: 0 4px 12px rgba(0,0,0,0.35);
        }
        .sperrung-closure .sperrung-pulse { background: #DC2626; }
        .sperrung-closure .sperrung-core   { background: #DC2626; }
        .sperrung-warning .sperrung-pulse { background: #F59E0B; }
        .sperrung-warning .sperrung-core   { background: #F59E0B; }
        .sperrung-detour  .sperrung-pulse { background: #2563EB; }
        .sperrung-detour  .sperrung-core   { background: #2563EB; }
        @keyframes sperrungPulse {
          0%   { transform: scale(1);   opacity: 0.55; }
          70%  { transform: scale(1.9); opacity: 0; }
          100% { transform: scale(1.9); opacity: 0; }
        }
      `}</style>
    </div>
  );
}
